import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { hasValidToken, refreshAuthToken } from '~/services/auth-service';

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  retryAuthentication: () => Promise<boolean>;
  ensureAuthenticated: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthState, setIsAuthState] = useState(hasValidToken());
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showRetryDialog, setShowRetryDialog] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { t } = useTranslation();

  // Check authentication status periodically
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = hasValidToken();
      const previousState = isAuthState;

      if (previousState !== authenticated) {
        setIsAuthState(authenticated);
      }
      return authenticated;
    };

    // Initial check immediately
    checkAuth();

    const authCheckInterval = setInterval(checkAuth, 10000);

    return () => {
      clearInterval(authCheckInterval);
    };
  }, [isAuthState]);

  // Listen for auth success events
  useEffect(() => {
    const handleAuthSuccess = () => {
      // Check authentication after success event
      const authenticated = hasValidToken();
      setIsAuthState(authenticated);

      if (authenticated) {
        setShowRetryDialog(false);
      }
    };

    window.addEventListener('auth:success', handleAuthSuccess);

    return () => {
      window.removeEventListener('auth:success', handleAuthSuccess);
    };
  }, [isAuthenticating]);

  // Listen for auth failures from API calls
  useEffect(() => {
    const handleAuthFailure = () => {
      if (!isAuthenticating) {
        setShowRetryDialog(true);
        setAuthError(t('errors.sessionExpired'));
      }
    };

    window.addEventListener('auth:failure', handleAuthFailure);

    return () => {
      window.removeEventListener('auth:failure', handleAuthFailure);
    };
  }, [isAuthenticating, t]);

  // Function to retry authentication
  const retryAuthentication = useCallback(async (): Promise<boolean> => {
    setIsAuthenticating(true);
    setAuthError(null);

    try {
      const success = await refreshAuthToken();

      if (success) {
        setIsAuthState(true);
        setShowRetryDialog(false);
        return true;
      } else {
        const errorMsg = t('errors.authRetryFailed');
        setAuthError(errorMsg);
        return false;
      }
    } catch (error) {
      setAuthError(
        error instanceof Error ? error.message : t('errors.authRetryFailed')
      );
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  }, [t]);

  // Function to ensure authentication before an operation
  const ensureAuthenticated = useCallback(async (): Promise<boolean> => {
    if (hasValidToken()) {
      setIsAuthState(true);
      return true;
    }

    return retryAuthentication();
  }, [retryAuthentication]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: isAuthState,
        isAuthenticating,
        retryAuthentication,
        ensureAuthenticated,
      }}
    >
      {children}
      {showRetryDialog && (
        <AuthRetryDialog
          error={authError}
          onRetry={retryAuthentication}
          isRetrying={isAuthenticating}
          onClose={() => {
            setShowRetryDialog(false);
          }}
        />
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Auth retry dialog component
function AuthRetryDialog({
  error,
  onRetry,
  isRetrying,
  onClose,
}: {
  error: string | null;
  onRetry: () => Promise<boolean>;
  isRetrying: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const handleRetry = async () => {
    await onRetry();
  };

  // Critical fix: This ensures the modal background doesn't
  // inadvertently capture events meant for elements underneath
  const handleModalClick = (e: React.MouseEvent) => {
    // Only let clicks on the actual modal background close it
    // and prevent these from reaching elements underneath
    if (e.target === e.currentTarget) {
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleModalClick} // Add the click handler to the overlay
      data-testid="auth-retry-dialog"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-gray-800">
        <h2 className="mb-2 text-xl font-bold">{t('security.authRequired')}</h2>
        <p className="mb-4">{error || t('security.sessionExpired')}</p>
        <div className="flex justify-between">
          <button
            className="bg-primary text-primary-foreground rounded px-4 py-2 disabled:opacity-50"
            onClick={handleRetry}
            disabled={isRetrying}
          >
            {isRetrying ? t('common.retrying') : t('common.retry')}
          </button>

          <button
            className="rounded border border-gray-300 px-4 py-2"
            onClick={onClose}
          >
            {t('common.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}
